import React from 'react';
import { Award, Shield, Clock, Users, Star, CheckCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const TrustSection = () => {
  const achievements = [
    {
      icon: Users,
      number: "10,000+",
      label: "Happy Students",
      description: "Successfully placed globally",
      color: "from-[#1565C0] to-[#0D47A1]"
    },
    {
      icon: Award,
      number: "95%",
      label: "Visa Success",
      description: "Industry-leading approval rate",
      color: "from-[#8BC34A] to-[#7CB342]"
    },
    {
      icon: Clock,
      number: "15+",
      label: "Years Experience",
      description: "Trusted expertise since 2010",
      color: "from-[#FF7043] to-[#F4511E]"
    },
    {
      icon: Star,
      number: "4.9/5",
      label: "Client Rating",
      description: "Based on 5000+ reviews",
      color: "from-[#1565C0] to-[#8BC34A]"
    }
  ];

  const certifications = [
    {
      name: "ICEF Certified",
      description: "International Consultants for Education and Fairs",
      badge: "🏆"
    },
    {
      name: "QEAC Registered",
      description: "Quality Education Agent Counsellors",
      badge: "✓"
    },
    {
      name: "British Council",
      description: "Certified UK Education Counsellor",
      badge: "🎓"
    },
    {
      name: "PIER Approved",
      description: "Professional International Education Resources",
      badge: "⭐"
    }
  ];

  const whyChoose = [
    {
      icon: Shield,
      title: "100% Transparent Process",
      description: "No hidden fees. Clear communication at every step. You always know what's happening with your application.",
      color: "text-[#1565C0]"
    },
    {
      icon: Users,
      title: "Dedicated Counselors",
      description: "Your personal counselor stays with you from day one until you land abroad. One point of contact, complete support.",
      color: "text-[#8BC34A]"
    },
    {
      icon: Award,
      title: "Certified Professionals",
      description: "Our team holds international certifications and receives regular training on latest immigration policies.",
      color: "text-[#FF7043]"
    },
    {
      icon: CheckCircle,
      title: "Proven Track Record",
      description: "15 years of success stories. Thousands of students now thriving in their dream countries thanks to our guidance.",
      color: "text-[#1565C0]"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-10 w-96 h-96 bg-[#1565C0] rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-[#8BC34A] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-[#8BC34A] to-[#7CB342] text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
              Trust & Excellence
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Why 10,000+ Students Trust Visaspot
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our commitment to excellence and student success has made us one of India's most trusted education consultants.
          </p>
        </div>

        {/* Achievement Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {achievements.map((achievement, index) => {
            const IconComponent = achievement.icon;
            return (
              <Card 
                key={index}
                className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 bg-white overflow-hidden group hover:-translate-y-2"
              >
                <CardContent className="p-8 text-center relative">
                  {/* Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${achievement.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                  
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${achievement.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-500`}>
                    <IconComponent size={32} className="text-white" />
                  </div>

                  {/* Number */}
                  <div className={`text-4xl font-bold bg-gradient-to-r ${achievement.color} bg-clip-text text-transparent mb-2`}>
                    {achievement.number}
                  </div>

                  {/* Label */}
                  <div className="text-lg font-bold text-gray-900 mb-1">
                    {achievement.label}
                  </div>

                  {/* Description */}
                  <div className="text-sm text-gray-600">
                    {achievement.description}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Why Choose Us Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20 max-w-6xl mx-auto">
          {whyChoose.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div 
                key={index}
                className="flex gap-6 p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`flex-shrink-0 ${item.color}`}>
                  <IconComponent size={40} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Certifications */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-12 border-2 border-blue-100">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recognized & Certified Globally
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our certifications ensure you receive guidance from qualified professionals who stay updated with the latest immigration policies.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="text-5xl mb-4">{cert.badge}</div>
                <div className="font-bold text-gray-900 mb-2">{cert.name}</div>
                <div className="text-sm text-gray-600">{cert.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Badge */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-4 bg-gradient-to-r from-[#8BC34A]/10 to-[#1565C0]/10 px-8 py-6 rounded-2xl border-2 border-[#8BC34A]/20">
            <div className="flex -space-x-3">
              <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330" alt="Student" className="w-12 h-12 rounded-full border-4 border-white object-cover shadow-lg" />
              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d" alt="Student" className="w-12 h-12 rounded-full border-4 border-white object-cover shadow-lg" />
              <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80" alt="Student" className="w-12 h-12 rounded-full border-4 border-white object-cover shadow-lg" />
              <div className="w-12 h-12 rounded-full border-4 border-white bg-gradient-to-r from-[#FF7043] to-[#F4511E] flex items-center justify-center text-white text-sm font-bold shadow-lg">
                +10K
              </div>
            </div>
            <div className="text-left">
              <div className="font-bold text-gray-900 text-lg">Join Our Success Community</div>
              <div className="text-gray-600">Trusted by students across India since 2010</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
